'use client'; // for server side rendering

import { AppBar, Toolbar, styled, useColorScheme } from '@mui/material';
import type { AppBarProps } from '@mui/material';
import { useThemeProps } from '@mui/material/styles';
import varnishTokens from '@allenai/varnish-theme/mui';
import { forwardRef, type PropsWithChildren, type ReactNode } from 'react';

interface HeaderProps extends Omit<AppBarProps, 'ref' | 'position'> {
    logo?: ReactNode;
    navItem?: ReactNode;
    search?: ReactNode;
    action?: ReactNode;

    position?: 'sticky' | 'fixed';
    shape?: 'box' | 'rounded' | 'full';

    searchClassName?: string;
    actionClassName?: string;
    navClassName?: string;
    logoClassName?: string;
}

interface HeaderOwnerState {
    shape: 'box' | 'rounded' | 'full';
}

// --- Styled slots ---

const HeaderRoot = styled(AppBar, {
    name: 'MuiHeader',
    slot: 'root',
})<{ ownerState: HeaderOwnerState }>(({ theme, ownerState }) => ({
    border: 'none',
    ...(ownerState.shape === 'box' && { borderRadius: varnishTokens.radii.xl.value }),
    ...(ownerState.shape === 'rounded' && { borderRadius: varnishTokens.radii.full.value }),
    [theme.breakpoints.down('sm')]: {
        ...(ownerState.shape === 'box' && { borderRadius: varnishTokens.radii.md.value }),
    },
}));

const HeaderContainer = styled('div', {
    name: 'MuiHeader',
    slot: 'container',
})({
    color: 'inherit',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
    width: '100%',
});

const HeaderLogo = styled('div', {
    name: 'MuiHeader',
    slot: 'logo',
})(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexShrink: 0,
}));

const HeaderSlotsArea = styled('div', {
    name: 'MuiHeader',
    slot: 'slotsArea',
})({
    color: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: varnishTokens.spacing[2].value,
    flexDirection: 'row',
});

const HeaderNav = styled('nav', {
    name: 'MuiHeader',
    slot: 'nav',
})({
    display: 'flex',
    gap: varnishTokens.spacing[2].value,
    alignSelf: 'center',
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
    color: 'inherit',
    flexDirection: 'row',
});

const HeaderSearch = styled('div', {
    name: 'MuiHeader',
    slot: 'search',
})({
    whiteSpace: 'nowrap',
    color: 'inherit',
});

const HeaderAction = styled('div', {
    name: 'MuiHeader',
    slot: 'action',
})({
    whiteSpace: 'nowrap',
    color: 'inherit',
});

const Header = forwardRef<HTMLDivElement, PropsWithChildren<HeaderProps>>(
    function Header(inProps, ref) {
        const props = useThemeProps({ props: inProps, name: 'MuiHeader' });
        const {
            children,
            logo,
            navItem,
            search,
            action,
            position = 'sticky',
            shape = 'box',
            searchClassName,
            actionClassName,
            navClassName,
            logoClassName,
            ...appBarProps
        } = props;

        const hasSlots = logo || navItem || search || action;
        const { mode, systemMode } = useColorScheme();
        const resolvedMode = mode === 'system' ? systemMode : mode;
        const invertedAttr = resolvedMode === 'dark' ? 'data-light' : 'data-dark';

        const ownerState: HeaderOwnerState = { shape };

        return (
            <HeaderRoot {...appBarProps} ref={ref} position={position} ownerState={ownerState}>
                <Toolbar>
                    {hasSlots ? (
                        <HeaderContainer {...{ [invertedAttr]: '' }}>
                            <HeaderLogo aria-label="Site Logo" className={logoClassName}>
                                {logo}
                            </HeaderLogo>
                            <HeaderSlotsArea>
                                {!!navItem && (
                                    <HeaderNav
                                        className={navClassName}
                                        aria-label="Main Navigation">
                                        {navItem}
                                    </HeaderNav>
                                )}
                                {!!search && (
                                    <HeaderSearch
                                        className={searchClassName}
                                        aria-label="Search Bar">
                                        {search}
                                    </HeaderSearch>
                                )}
                                {!!action && (
                                    <HeaderAction
                                        className={actionClassName}
                                        aria-label="Action Button">
                                        {action}
                                    </HeaderAction>
                                )}
                            </HeaderSlotsArea>
                        </HeaderContainer>
                    ) : (
                        children
                    )}
                </Toolbar>
            </HeaderRoot>
        );
    }
);

export { Header };
export type { HeaderProps };
